import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { MFASession } from "../services/mfaService";
import type { IUser } from "@features/users";
import { useNavigate } from "react-router";
import { useAuthStore } from "@stores/auth.store";
import api, { stopAutoRefresh, startAutoRefresh, performRefresh, log as apiLog } from "@services/newApi/api";
import newTokenService from "../services/newTokenService";
import mfaService from "../services/mfaService";
import sessionSyncService from "../services/sessionSyncService";
import { clearPreviousIdentityState, clearSessionState, clearSessionStorage } from "../services/sessionState";
import csrfService from "../api/csrfService";
import authLogger from "../services/authLogger";
import { sanitizeErrorMessage } from "../utils/authErrorHandler";
import { toast } from "react-toastify";
import type {
  IAuthContextData,
  ICreatePatientBody,
  ICreatePatientResponse,
  IGetByCpfResponse,
  ISendCodeBody,
  ISendCodeResponse,
  ISession,
  IUpdateInternalDoctorMySQLDataParams,
  IUpdateInternalDoctorParams,
  IUpdateInternalDoctorResponse,
} from "./types";

export const AuthContext = createContext<IAuthContextData>(
  {} as IAuthContextData
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isExpiredTokenModalOpen, setIsExpiredTokenModalOpen] = useState(false);
  const [activeSessions, setActiveSessions] = useState<ISession[]>([]);

  // Estado de auth migrado para o store global (Task 2.6)
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  const navigate = useNavigate();

  // ========================================
  // ATUALIZAÇÃO DE USUÁRIO
  // ========================================
  const updateUser = useCallback((newUser: IUser | null) => {
    if (!newUser) {
      useAuthStore.getState().setUser(null);
      localStorage.removeItem("new_auth:user");
      return;
    }

    setIsExpiredTokenModalOpen(false);
    useAuthStore.getState().setUser(newUser);
    localStorage.setItem("new_auth:user", JSON.stringify({
      id: newUser.id,
      role_id: newUser.role_id,
    }));
  }, []);

  // Adota, SEM reload, uma sessão já emitida pelo backend (token no corpo da
  // resposta) — usado pela personificação (switch-user/back). É o mesmo passo de
  // adoção do signInUnified; a personificação é o 3º/4º consumidor previsto na
  // nota "applyDirectLogin". Antes o switch fazia window.location.href: o reload
  // descartava o access token (só vive em memória) e a sessão era reconstruída
  // como o ADMIN (cache stale + cookie de refresh do admin) → role_id 1 / 243
  // hospitais. Adotar o token do alvo em memória resolve na raiz; o
  // ActiveHospitalProvider reage à troca de auth.user e re-hidrata o hospital.
  const applyDirectSession = useCallback(
    (data: {
      accessToken: string;
      expiresAt?: number;
      user: IUser;
      csrfToken?: string;
    }) => {
      newTokenService.setTokens(data.accessToken, data.expiresAt);
      csrfService.setFromResponse(data);
      updateUser(data.user);
      api.defaults.headers.common.Authorization = `Bearer ${data.accessToken}`;
      startAutoRefresh();
    },
    [updateUser]
  );

  // ========================================
  // LOGOUT
  // ========================================
  const logout = useCallback(async () => {
    authLogger.warn("logout", "logout iniciado", { motivo: "user-action" });

    // 1) Limpeza LOCAL primeiro (síncrono) — UX instantâneo.
    // Antes esperávamos o /auth/logout retornar pra fazer o cleanup no finally,
    // mas se a request travasse (CSRF fetch lento, backend offline), o navigate
    // nunca disparava e o usuário ficava preso na tela.
    // Tokens, MFA, cache de queries (escopado à identidade que sai — mesma razão do
    // switch-user, useSwitchUser.ts:30), hospital ativo e id guardados: tudo no mesmo lugar,
    // para que "Sair" e os outros fins de sessão limpem a mesma coisa.
    clearSessionState();
    useAuthStore.getState().reset();
    localStorage.clear();
    delete api.defaults.headers.common.Authorization;
    stopAutoRefresh();
    sessionSyncService.emit("LOGOUT");

    // 2) Redireciona imediatamente.
    navigate("/login", { replace: true });

    // 3) Notifica backend fire-and-forget (best-effort).
    // Erro/timeout aqui não afeta o usuário — ele já está deslogado localmente.
    api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {
      apiLog.warn("auth", "Logout no servidor falhou — sessão local já foi limpa");
    });
  }, [navigate]);

  const logoutAll = useCallback(async () => {
    authLogger.warn("logout", "logout-all iniciado", { motivo: "user-action" });
    try {
      // Notifica outras abas (e o portal-senneliquor)
      sessionSyncService.emit("LOGOUT_ALL");
      
      // Logout global no servidor
      await api.post("/auth/logout-all");
      toast.success("Você foi desconectado de todos os dispositivos.");
    } catch (error: any) {
      toast.error(sanitizeErrorMessage(error.response?.data?.message || "") || "Erro ao realizar logout global");
    } finally {
      logout();
    }
  }, [logout]);

  // ========================================
  // HANDLERS DE EVENTOS
  // ========================================
  const handleTokenExpired = useCallback(() => {
    apiLog.warn("auth", "Token expirado - evento disparado");
    setIsExpiredTokenModalOpen(true);
    stopAutoRefresh();
  }, []);

  const handleSessionRevoked = useCallback(() => {
    apiLog.warn("auth", "Sessão revogada - evento disparado");
    authLogger.warn("logout", "session:revoked — emitindo SESSION_REVOKED");
    toast.error("Sua sessão foi encerrada por segurança.");
    logout();
  }, [logout]);

  // Registra listeners de eventos
  useEffect(() => {
    window.addEventListener("token:expired", handleTokenExpired);
    window.addEventListener("session:revoked", handleSessionRevoked);

    // Sincronização entre abas e com o portal-senneliquor (canal "senne-auth")
    sessionSyncService.subscribe((event) => {
      switch (event.type) {
        case "LOGIN":
        case "LOGIN_INHERITED": {
          const userString = localStorage.getItem("new_auth:user");
          if (userString) {
            try {
              updateUser(JSON.parse(userString));
            } catch {
              localStorage.removeItem("new_auth:user");
              return;
            }
            startAutoRefresh();
          }
          break;
        }
        case "LOGOUT":
        case "LOGOUT_ALL":
          logout();
          break;
        case "REFRESH":
        case "TOKEN_REFRESHED":
          // NÃO refrescar aqui. Refrescar em resposta a "outra aba refrescou" emite
          // TOKEN_REFRESHED de novo, a outra aba recebe e refresca, e vira laço: com duas abas
          // abertas o console mostrava REFRESH_START a menos de um segundo de distância, em
          // sequência. E como o refresh token ROTACIONA, as corridas invalidam umas às outras até
          // uma falhar e a sessão morrer.
          //
          // Não é preciso fazer nada: o access token vive em memória POR ABA e é um JWT
          // autocontido — a rotação do cookie da outra aba não o invalida. Se ficar obsoleto, o
          // interceptor de 401 (`newApi/api.ts`) refresca e repete a requisição sob demanda, e o
          // auto-refresh de 15min continua valendo. O evento serve para saber que o cookie girou,
          // não como gatilho de trabalho.
          break;
        case "SESSION_REVOKED":
          logout();
          break;
      }
    });

    return () => {
      window.removeEventListener("token:expired", handleTokenExpired);
      window.removeEventListener("session:revoked", handleSessionRevoked);
    };
  }, [handleTokenExpired, handleSessionRevoked, logout, updateUser]);

  // ========================================
  // RESTAURAÇÃO DE SESSÃO (MOUNT)
  // ========================================
  useEffect(() => {
    let mounted = true;

    // Sessão que o servidor recusou não vira sessão degradada. Antes, qualquer resposta sem
    // `user` (ou qualquer 4xx) caía no cache local `{id, role_id}` e a pessoa ficava dentro do
    // portal como uma sessão anônima: sem nome no cabeçalho, sem `roles`, sem `permissoes` e
    // com a home vazia — inclusive para conta inativa (status 0) ou aguardando aprovação (3).
    const endRefusedSession = (message: string) => {
      if (!mounted) return;
      clearSessionStorage();
      useAuthStore.getState().setUser(null);
      stopAutoRefresh();
      toast.info(message, { toastId: "sessao-recusada" });
    };

    const restoreSession = async () => {
      try {
        const cachedToken = newTokenService.getAccessToken();
        const cachedUserString = localStorage.getItem("new_auth:user");

        if (!cachedToken && !cachedUserString) {
          if (mounted) {
            useAuthStore.getState().setUser(null);
            useAuthStore.getState().setLoading(false);
          }
          return;
        }

      if (!cachedToken && cachedUserString) {
        try {
          const newToken = await performRefresh();

          if (mounted && newToken) {
            api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            startAutoRefresh();

            try {
              const validationResponse = await api.post("/auth/validation");
              // /auth/validation (200) devolve o user completo (id/name/role do
              // ALVO quando personificando) — não expõe um campo `valid`.
              if (validationResponse.data.user) {
                updateUser(validationResponse.data.user);
              } else {
                // 200 sem `user` = o backend recusou a sessão. Hoje é o caso do status 4
                // (telefone por validar): `validation.usecase.ts` corta a resposta e devolve
                // só o celular mascarado, esperando que o fluxo de validação continue.
                endRefusedSession(
                  "Sua conta ainda precisa validar o telefone. Entre novamente para concluir.",
                );
                return;
              }
            } catch (validationError) {
              // 4xx = o servidor respondeu e recusou (conta inativa, aguardando aprovação,
              // senha por cadastrar). 5xx ou falha de rede = não dá para concluir nada sobre
              // a sessão; só aí vale seguir com o cache mínimo.
              const status = (validationError as { response?: { status?: number } })?.response
                ?.status;
              if (status !== undefined && status >= 400 && status < 500) {
                const message = (
                  validationError as { response?: { data?: { message?: string } } }
                )?.response?.data?.message;
                endRefusedSession(
                  sanitizeErrorMessage(message ?? "") ||
                    "Sua sessão não é mais válida. Entre novamente.",
                );
                return;
              }
              const userData = JSON.parse(cachedUserString);
              updateUser(userData);
            }
          }
          if (mounted) useAuthStore.getState().setLoading(false);
          return;
          } catch (refreshError) {
            if (mounted) {
              // Só o servidor recusar o refresh (401/403) encerra a sessão. Rede, timeout
              // (cold start do backend chega a ~25 s), 5xx, 409 depois dos retries: nada disso
              // diz que o cookie é inválido — fica sem token (a UI cai no /login via guard), mas
              // `new_auth:user` e o hospital ficam, e o próximo reload recupera pelo cookie.
              const refreshStatus = (refreshError as { response?: { status?: number } })?.response
                ?.status;
              if (refreshStatus === 401 || refreshStatus === 403) {
                clearSessionStorage();
                useAuthStore.getState().setUser(null);
              } else {
                // Spec 20261028-130500 AC-8: a failure that is not a refusal keeps the person on the page with the
                // cached user; the next call's 401 path refreshes again. It sent them to /login before (handoff of
                // 2026-10-28; test change approved by the owner the same day).
                newTokenService.clearTokens();
                updateUser(JSON.parse(cachedUserString));
              }
              useAuthStore.getState().setLoading(false);
            }
            return;
          }
        }

      // Validar sessão via endpoint de validação do novo backend
      // Dados do servidor são sempre autoritativos — nunca sobrescrever com cache local
      try {
        const validationResponse = await api.post("/auth/validation");
        if (validationResponse.data.user) {
          if (mounted) {
            updateUser(validationResponse.data.user);
            api.defaults.headers.common.Authorization = `Bearer ${cachedToken}`;
            startAutoRefresh();
          }
        } else {
          throw new Error("Sessão inválida");
        }
    } catch (e) {
      if (newTokenService.isTokenExpired()) {
        try {
          const newToken = await performRefresh();
              if (mounted && newToken) {
                api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
                startAutoRefresh();
              }
            } catch {
              throw e;
            }
          } else {
            throw e;
          }
        }
      } catch (error) {
        console.error("❌ Erro ao restaurar sessão:", (error as any)?.message);
        if (mounted) {
          // Só o servidor recusar (4xx) encerra a sessão de vez. Falha de rede/5xx no bootstrap
          // mantém `new_auth:user` e o hospital: o próximo reload ainda pode recuperar pelo
          // cookie de refresh, e a regra do Portal 2.0 é que só "Sair" ou recusa encerram.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== undefined && status >= 400 && status < 500) {
            clearSessionStorage();
          } else {
            newTokenService.clearTokens();
          }
          useAuthStore.getState().setUser(null);
          stopAutoRefresh();
        }
      } finally {
        if (mounted) {
          useAuthStore.getState().setLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      mounted = false;
    };
  }, [updateUser]);

  // ========================================
  // REFRESH MANUAL
  // ========================================
  const refreshToken = useCallback(async () => {
    try {
      const newToken = await performRefresh();
      if (newToken) {
        setIsExpiredTokenModalOpen(false);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        startAutoRefresh();
      }
    } catch (error) {
      console.error("❌ Erro ao renovar token:", (error as any)?.message);
      logout();
    }
  }, [logout]);

  // ========================================
  // SIGN IN UNIFIED (POST /auth/login, sem roleId)
  // ========================================
  // O único login: só o identificador, e o código depois (backend spec 20261021-141500, AC-3). O `signIn` por papel
  // (`/auth/login/:id`), de quem este bloco duplicava o apply do 200, saiu com as senhas.
  const signInUnified = useCallback(
    async (payload: { identifier: string }) => {
      const response = await api.post("/auth/login", { identifier: payload.identifier });
      const {
        requireMFA, mfaToken, userId, accessToken, user: userData, expiresAt, mfaExpiresAt,
        cellphone, cellphoneMasked, email: emailFromBody, emailMasked,
      } = response.data;

      if (requireMFA) {
        const hasMasked = typeof emailMasked !== "undefined" || typeof cellphoneMasked !== "undefined";
        const finalCellMasked = hasMasked ? cellphoneMasked ?? null : cellphone ?? null;
        const finalEmailMasked = hasMasked ? emailMasked ?? null : emailFromBody ?? null;
        const synthChannels: { type: "sms" | "whatsapp" | "email"; masked: string; sent: boolean }[] = [];
        if (finalCellMasked) { synthChannels.push({ type: "sms", masked: finalCellMasked, sent: true }); synthChannels.push({ type: "whatsapp", masked: finalCellMasked, sent: true }); }
        if (finalEmailMasked) synthChannels.push({ type: "email", masked: finalEmailMasked, sent: true });
        const mfaSession: MFASession = {
          mfaToken, userId: userId ?? userData?.id ?? 0, roleId: userData?.role_id ?? 0,
          expiresAt: new Date(mfaExpiresAt), cellphone: hasMasked ? cellphone ?? "" : "",
          email: hasMasked ? emailFromBody ?? "" : "",
          cellphoneMasked: finalCellMasked ?? undefined, emailMasked: finalEmailMasked ?? undefined,
          channels: synthChannels, attemptsRemaining: 5,
        };
        mfaService.setMFASession(mfaSession);
        return { requiresMFA: true as const, mfaSession, status: 202 as const };
      }

      clearPreviousIdentityState();
      newTokenService.setTokens(accessToken, expiresAt);
      csrfService.setFromResponse(response.data);
      const refreshFromBody = response.data.refreshToken || response.data.refresh_token;
      if (refreshFromBody) newTokenService.setRefreshToken(refreshFromBody);
      setIsExpiredTokenModalOpen(false);
      updateUser(userData);
      // Mesmo valor e mesma razão do signIn: o id desta sessão, não o da anterior.
      sessionStorage.setItem("User_id_senne", String(userData?.id));
      localStorage.removeItem("User_id_senne");
      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      startAutoRefresh();
      sessionSyncService.emit("LOGIN", { user: userData });
      return { requiresMFA: false as const, user: userData, status: 200 as const };
    },
    [updateUser]
  );

  // ========================================
  // COMPLETE MFA
  // ========================================
  // Verificação rica: devolve o motivo da recusa (status 401/423, tentativas
  // restantes, retryAfter) para quem mostra o estado na tela. `completeMFA`
  // (boolean) continua existindo para o ValidateSMS; o fluxo de login usa este,
  // porque o boolean descartava o 401/423 e as tentativas nunca decrementavam
  // nem o lockout aparecia (P20EE-141).
  const verifyMFA = useCallback(
    async (
      code: string,
      _role_id: number
    ): Promise<{
      ok: boolean;
      status?: number | undefined;
      message?: string | undefined;
      retryAfterSeconds?: number | undefined;
      attemptsRemaining?: number | undefined;
    }> => {
      const session = mfaService.getMFASession();
      if (!session) {
        return { ok: false, message: "Sessão MFA expirada" };
      }

      const result = await mfaService.verifyMFACode(code, session.mfaToken);
      if (result.success && result.token) {
        clearPreviousIdentityState();
        newTokenService.setTokens(result.token, result.expiresAt);
        if (result.refreshToken) {
          newTokenService.setRefreshToken(result.refreshToken);
        }
        updateUser(result.user);
        api.defaults.headers.common.Authorization = `Bearer ${result.token}`;
        startAutoRefresh();
        return { ok: true };
      }

      const message = result.error || "Código MFA inválido";
      // O backend informa as tentativas restantes no texto do 401
      // ("Código inválido. N tentativa(s) restante(s).").
      const match = message.match(/(\d+)\s*tentativ/);
      const attemptsRemaining = match ? parseInt(match[1]!, 10) : undefined;
      return {
        ok: false,
        status: result.status,
        message,
        retryAfterSeconds: result.retryAfterSeconds,
        attemptsRemaining,
      };
    },
    [updateUser]
  );

  const completeMFA = useCallback(
    async (code: string, role_id: number): Promise<boolean> => {
      try {
        const result = await verifyMFA(code, role_id);
        if (!result.ok) {
          toast.error(result.message || "Código MFA inválido");
        }
        return result.ok;
      } catch (error) {
        console.error("❌ Erro ao completar MFA:", (error as any)?.message);
        return false;
      }
    },
    [verifyMFA]
  );

  // ========================================
  // REFRESH USER DATA
  // ========================================
const refreshUserData = useCallback(async () => {
  if (!user?.id) return;
  try {
    const response = await api.get(`/users/${user.id}`);
    updateUser(response.data.data.users);
  } catch (error) {
    console.error("❌ Erro ao atualizar dados do usuário:", (error as any)?.message);
  }
}, [user?.id, updateUser]);

  // ========================================
  // OUTRAS FUNÇÕES (Mantidas para compatibilidade)
  // ========================================
  // ponytail: sem consumidor desde que a confirmação do pré-paciente deixou de
  // chamar /auth/validation (rota de sessão, que ignora CPF e nascimento). Mantido
  // porque está no contrato do contexto; remover exige tirar de types.ts junto.
  const validateUser = useCallback(async (data: any) => {
    try {
      const response = await api.post("/auth/validation", data);
      return response;
    } catch (error: any) {
      toast.error(sanitizeErrorMessage(error?.response?.data?.message || "") || "Erro ao validar sessão");
      throw error;
    }
  }, []);

  const updateInternalDoctor = useCallback(
    async (formToSend: IUpdateInternalDoctorParams) => {
      try {
        const response = await api.put<IUpdateInternalDoctorResponse>(
          `/internal-doctors/tasy`,
          formToSend
        );
        return response.data;
    } catch (error: any) {
      if (error?.response?.data?.message)
        toast.error(sanitizeErrorMessage(error?.response?.data?.message));
      return error?.response?.data;
    }
  },
  []
);

  const updateInternalDoctorMySQLData = useCallback(
  async (formToSend: IUpdateInternalDoctorMySQLDataParams) => {
    try {
      const response = await api.put<IUpdateInternalDoctorResponse>(
        `/internal-doctors/${formToSend.user_id}/complement`,
        formToSend
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.data?.message)
        toast.error(sanitizeErrorMessage(error?.response?.data?.message));
        return error?.response?.data;
      }
    },
    []
  );

const updateUserLogged = useCallback(
  async (params: Partial<IUser>, userId: number) => {
    try {
      const response = await api.put<{ data: { users: IUser }; message: string }>(
        `/users/${userId}`,
        params
      );
      toast.success(response.data.message);

      const prevUser = useAuthStore.getState().user;
      const merged = { ...prevUser, ...response.data.data.users } as IUser;
      localStorage.setItem("new_auth:user", JSON.stringify({
        id: merged.id,
        role_id: merged.role_id,
      }));
      useAuthStore.getState().setUser(merged);
    } catch (error: any) {
      toast.error(
        sanitizeErrorMessage(error?.response?.data?.message || "") || "Erro ao atualizar usuário"
      );
    }
  },
  []
);

  const loadActiveSessions = useCallback(async () => {
    try {
      const response = await api.get<{ sessions: ISession[] }>("/auth/sessions");
      setActiveSessions(response.data.sessions);
    } catch (error: any) {
      console.error("❌ Erro ao carregar sessões:", error?.message);
      toast.error(sanitizeErrorMessage(error.response?.data?.message || "") || "Erro ao carregar sessões");
    }
  }, []);

  const revokeSession = useCallback(
    async (sessionId: number) => {
      try {
        await api.delete(`/auth/revoke-session/${sessionId}`);
        toast.success("Sessão revogada com sucesso");
        await loadActiveSessions();
      } catch (error: any) {
        toast.error(sanitizeErrorMessage(error.response?.data?.message || "") || "Erro ao revogar sessão");
      }
    },
    [loadActiveSessions]
  );

  const getByCpf = useCallback(async (cpf: string) => {
    try {
      // O backend responde no envelope { message, data: {...} }; o next_step
      // vem em response.data.data (mesmo padrão de refreshUserData/updateUserLogged).
      const response = await api.post<{ message: string; data: IGetByCpfResponse }>(
        "/auth/validate-identifier",
        { identifier: cpf }
      );
      return response.data.data;
    } catch (error: any) {
      toast.error(sanitizeErrorMessage(error?.response?.data?.message || "") || "Erro ao validar CPF");
      throw error;
    }
  }, []);

  const createPatient = useCallback(async (body: ICreatePatientBody) => {
    try {
      const response = await api.post<ICreatePatientResponse>(
        "/auth/create-patient",
        body
      );
      sessionStorage.setItem("User_id_senne", response.data.id.toString());
  localStorage.removeItem("User_id_senne");
      return response.data;
    } catch (error: any) {
      toast.error(sanitizeErrorMessage(error?.response?.data?.message || "") || "Erro ao criar paciente");
      throw error;
    }
  }, []);

  const sendCode = useCallback(
    async (body: ISendCodeBody) => {
      try {
        const response = await api.post<ISendCodeResponse>(
          "/auth/send-code",
          body
        );
        return response.data;
      } catch (error: any) {
        toast.error(sanitizeErrorMessage(error?.response?.data?.message || "") || "Erro ao enviar código");
        navigate("/login");
        throw error;
      }
    },
    [navigate]
  );

  // ========================================
  // CONTEXT VALUE
  // ========================================
  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: !!user && newTokenService.hasValidToken(),
      isLoading,
      signInUnified,
      logout,
      logoutAll,
      validateUser,
      completeMFA,
      verifyMFA,
      refreshUserData,
      isExpiredTokenModalOpen,
      setIsExpiredTokenModalOpen,
      setUser: updateUser,
      updateInternalDoctor,
      updateInternalDoctorMySQLData,
      updateUserLogged,
      activeSessions,
      loadActiveSessions,
      revokeSession,
      getByCpf,
      createPatient,
      sendCode,
      updateUser,
      refreshToken,
      applyDirectSession,
    }),
    [
      user,
      isLoading,
      signInUnified,
      applyDirectSession,
      logout,
      logoutAll,
      validateUser,
      completeMFA,
      verifyMFA,
      refreshUserData,
      isExpiredTokenModalOpen,
      updateUser,
      updateInternalDoctor,
      updateInternalDoctorMySQLData,
      updateUserLogged,
      activeSessions,
      loadActiveSessions,
      revokeSession,
      getByCpf,
      createPatient,
      sendCode,
      updateUser,
      refreshToken,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
